import { Fragment, useMemo, useState } from 'react';

import { IHeaderViewModel } from 'Types/Litium';
import Divider from 'Components/Divider';
import CountrySelector from 'Components/Header/CountrySelector';

import CaretIcon from 'Icons/caretGsd.svg?react';

interface MobileMenuProps {
    isOpen: boolean;
    offset: number;
    menuItems: IHeaderViewModel['navigation']['contentLinks'];
    linkList: IHeaderViewModel['additionalLinkList'];
    currentCulture: IHeaderViewModel['currentCulture'];
    evergreenLinkList: IHeaderViewModel['mobileMenuEvergreenLinkList'];
}

interface IMobileItems {
    url: string | null;
    name: string | null;
    links: IMobileItems[];
    hasLinks: boolean;
    textColourHexCode: string;
}

const mapMenuItems = (items: MobileMenuProps['menuItems']): IMobileItems[] =>
    items
        .map(item => {
            // The links needs a name to be able to click on something
            const hasLinks =
                item.links?.some(
                    link =>
                        (link.url && link.name) ||
                        (link.links?.length > 0 && link.name)
                ) ?? false;

            return {
                url: item.url,
                name: item.name,
                hasLinks,
                textColourHexCode: item.textColourHexCode,
                // Recursively map and filter child links, ensuring non-null links are included
                links: hasLinks
                    ? mapMenuItems(item.links ?? []).filter(
                          link => link != null
                      )
                    : [],
            };
        })
        .filter(
            item => (item.name && item.url) || (item.name && item.hasLinks)
        ); // Ensure only actionable or meaningful items are included

const MobileMenu = ({
    isOpen = false,
    offset = 0,
    menuItems,
    linkList,
    currentCulture,
    evergreenLinkList,
}: MobileMenuProps) => {
    const [openMenus, setOpenMenus] = useState<string[]>([]);
    const toggleMobileMenu = (index: string) => {
        if (openMenus.includes(index)) {
            setOpenMenus(openMenus.filter(i => i !== index));
        } else {
            setOpenMenus([...openMenus, index]);
        }
    };

    const memoizedMenuItems = useMemo(
        () => mapMenuItems(menuItems),
        [menuItems]
    );

    return (
        <div
            style={
                { '--menu-height': `${offset + 48}px` } as React.CSSProperties
            }
            className={`absolute left-0 top-full z-50 grid h-[calc(100dvh-var(--menu-height))] w-full max-w-md -translate-x-full grid-rows-[auto,_min-content] overflow-hidden bg-white shadow-bold transition-all duration-layout lg:hidden ${
                isOpen ? 'visible translate-x-0 opacity-100' : 'invisible'
            }`}
        >
            <ul className="flex max-w-md flex-col gap-4 overflow-x-hidden pb-7 pt-4">
                {memoizedMenuItems.map((item, index) =>
                    item.hasLinks ? (
                        <li key={index}>
                            <ul className="grid gap-4.5">
                                {item.links.map((subItem, subIndex) => (
                                    <Fragment key={subIndex}>
                                        {subItem.name && subItem.hasLinks && (
                                            <li>
                                                <button
                                                    style={
                                                        {
                                                            '--text-color':
                                                                item.textColourHexCode ||
                                                                'text-black',
                                                        } as React.CSSProperties
                                                    }
                                                    className={`flex w-full items-center gap-2.5 px-4.5 text-left font-montserrat text-lg  ${
                                                        item.textColourHexCode
                                                            .length > 0
                                                            ? 'text-[var(--text-color)]'
                                                            : 'text-black'
                                                    }`}
                                                    onClick={() =>
                                                        toggleMobileMenu(
                                                            `${index} - ${subIndex}`
                                                        )
                                                    }
                                                >
                                                    <span className="flex-1">
                                                        {subItem.name}
                                                    </span>
                                                    <CaretIcon
                                                        className={`h-5 w-5 origin-center  transition-transform duration-layout ${
                                                            openMenus.includes(
                                                                `${index} - ${subIndex}`
                                                            )
                                                                ? 'rotate-90'
                                                                : 'rotate-0'
                                                        }`}
                                                    />
                                                </button>
                                                <div
                                                    className={`grid origin-top transition-[grid-template-rows,_opacity,_visibility] duration-layout ${
                                                        openMenus.includes(
                                                            `${index} - ${subIndex}`
                                                        )
                                                            ? 'opacity-1 visible grid-rows-[1fr]'
                                                            : 'invisible grid-rows-[0fr] opacity-0'
                                                    } `}
                                                >
                                                    <div className="overflow-hidden">
                                                        <ul className="grid gap-y-3.5 px-9 pt-4.5">
                                                            {subItem.links.map(
                                                                (
                                                                    subSubItem,
                                                                    subSubIndex
                                                                ) =>
                                                                    subSubItem.url && (
                                                                        <li
                                                                            key={
                                                                                subSubIndex
                                                                            }
                                                                        >
                                                                            <a
                                                                                className="w-full text-left font-roboto text-base font-light text-black"
                                                                                href={
                                                                                    subSubItem.url
                                                                                }
                                                                            >
                                                                                {
                                                                                    subSubItem.name
                                                                                }
                                                                            </a>
                                                                        </li>
                                                                    )
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </li>
                                        )}
                                    </Fragment>
                                ))}
                            </ul>
                        </li>
                    ) : (
                        item.url && (
                            <li key={index}>
                                <a
                                    style={
                                        {
                                            '--text-color':
                                                item.textColourHexCode ||
                                                'text-black',
                                        } as React.CSSProperties
                                    }
                                    className={`w-full px-4.5 text-left font-montserrat text-lg  ${
                                        item.textColourHexCode.length > 0
                                            ? 'text-[var(--text-color)]'
                                            : 'text-black'
                                    }`}
                                    href={item.url}
                                >
                                    {item.name}
                                </a>
                            </li>
                        )
                    )
                )}
                {evergreenLinkList?.map((item, index) => (
                    <li key={index}>
                        <a
                            className="w-full px-4.5 text-left font-montserrat text-lg"
                            href={item.href}
                        >
                            {item.text}
                        </a>
                    </li>
                ))}
                <li>
                    <Divider />
                </li>
                {linkList.map((item, index) => (
                    <li key={index}>
                        <a
                            className="w-full px-4.5 text-left font-roboto text-base font-normal"
                            href={item.href}
                        >
                            {item.text}
                        </a>
                    </li>
                ))}
                <li className="px-4.5">
                    <CountrySelector currentCulture={currentCulture} />
                </li>
            </ul>
        </div>
    );
};

export default MobileMenu;
